import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import { StandardPinChoice, StandardPinConfiguration, StandardPinScope } from '../../../models/standard-pin.model';
import { StandardPinService } from '../../../services/standard-pin.service';
import { filterStandardPins, standardPinChoices } from './standard-pins.presentation';
import { GroupContext } from '../../../models/group-context.model';

const PAGE_SIZE = 10;
const FIRST_PAGE = 0;
const NEXT_PAGE = 1;
const EMPTY_TEXT = '';
const HTTP_CONFLICT = 409;
const NO_ERROR = '';
const ERROR_LOAD = 'STANDARD_PINS_LOAD_ERROR';
const ERROR_SAVE = 'STANDARD_PINS_SAVE_ERROR';
const ERROR_CONFLICT = 'STANDARD_PINS_CONFLICT';
const DEPARTMENT_KIND = 'DEPARTMENT';
const CHILD_KIND = 'SUBDEPARTMENT';
const DEPARTMENT_PREFIX = 'department:';
const CHILD_LIMIT = 1;

@Component({
  selector: 'app-standard-pins',
  templateUrl: './standard-pins.component.html',
  styleUrls: ['./standard-pins.component.css'],
  providers: [StandardPinService]
})
export class StandardPinsComponent implements OnChanges, OnDestroy {
  @Input() groupId = EMPTY_TEXT;
  @Input() standards: StandardPinChoice[] = [];
  scopes: StandardPinScope[] = [];
  configuration: StandardPinConfiguration;
  selectedIds: string[] = [];
  scopeId = EMPTY_TEXT;
  query = EMPTY_TEXT;
  page = FIRST_PAGE;
  expanded = false;
  loading = false;
  saving = false;
  saved = false;
  requiresReload = false;
  errorKey = NO_ERROR;
  readonly pageSize = PAGE_SIZE;
  private readonly scopeChange$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(private pins: StandardPinService) {}

  ngOnChanges(changes): void {
    if (changes.groupId) { this.reloadScopes(); }
    if (changes.standards) { this.page = FIRST_PAGE; }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.scopeChange$.next();
    this.scopeChange$.complete();
  }

  reloadScopes(): void {
    const previousScope = this.scopeId;
    this.resetScope();
    this.scopes = [];
    this.pins.scopes(this.groupId).takeUntil(this.scopeChange$).takeUntil(this.destroy$).subscribe(
      scopes => this.loadScopeChildren(scopes, previousScope), () => this.loadFailed()
    );
  }

  selectScope(scopeId: string): void {
    this.resetScope();
    this.scopeId = scopeId;
    this.pins.read(scopeId).takeUntil(this.scopeChange$).takeUntil(this.destroy$).subscribe(
      configuration => this.acceptConfiguration(configuration), () => this.loadFailed()
    );
  }

  isSelected(choice: StandardPinChoice): boolean { return this.selectedIds.indexOf(choice.id) >= FIRST_PAGE; }

  isDisabled(choice: StandardPinChoice): boolean {
    return this.loading || this.saving || this.requiresReload || !this.configuration
      || (!this.isSelected(choice) && (choice.active === false || this.selectedIds.length >= this.configuration.limit));
  }

  toggle(choice: StandardPinChoice): void {
    if (this.isDisabled(choice)) { return; }
    this.selectedIds = this.isSelected(choice)
      ? this.selectedIds.filter(id => id !== choice.id) : this.selectedIds.concat(choice.id);
    this.saved = false;
    this.errorKey = NO_ERROR;
  }

  save(): void {
    if (!this.configuration || this.loading || this.saving || this.requiresReload) { return; }
    this.saving = true;
    this.saved = false;
    this.errorKey = NO_ERROR;
    const update = {version: this.configuration.version, standardIds: this.selectedIds.slice()};
    this.pins.save(this.scopeId, update).takeUntil(this.scopeChange$).takeUntil(this.destroy$).subscribe(
      configuration => { this.acceptConfiguration(configuration); this.saved = true; },
      error => this.saveFailed(error.status)
    );
  }

  search(value: string): void { this.query = value; this.page = FIRST_PAGE; }

  get matchingChoices(): StandardPinChoice[] {
    const saved = this.configuration ? this.configuration.standards : [];
    return filterStandardPins(standardPinChoices(saved, this.standards || []), this.query);
  }

  get visibleChoices(): StandardPinChoice[] {
    return this.matchingChoices.slice(this.page * PAGE_SIZE, (this.page + NEXT_PAGE) * PAGE_SIZE);
  }

  trackChoice(index: number, choice: StandardPinChoice): string { return choice.id; }

  trackScope(index: number, scope: StandardPinScope): string { return scope.scopeId; }

  private loadScopeChildren(scopes: StandardPinScope[], previousScope: string): void {
    const root = scopes.find(scope => scope.kind === DEPARTMENT_KIND);
    if (!root) { this.acceptScopes(scopes, previousScope); return; }
    const groupId = root.scopeId.substring(DEPARTMENT_PREFIX.length);
    this.pins.context(groupId).takeUntil(this.scopeChange$).takeUntil(this.destroy$).subscribe(context => {
      if (!context || context.groupId !== groupId || !context.canManageGroupSettings
        || !Array.isArray(context.subdepartments)) { this.loadFailed(); return; }
      this.acceptScopes(scopes.concat(this.childScopes(context)), previousScope);
    }, () => this.loadFailed());
  }

  private childScopes(context: GroupContext): StandardPinScope[] {
    return context.subdepartments.map((child): StandardPinScope => ({
      scopeId: DEPARTMENT_PREFIX + child.id, kind: CHILD_KIND, name: child.name, limit: CHILD_LIMIT
    }));
  }

  private acceptScopes(scopes: StandardPinScope[], previousScope: string): void {
    this.scopes = scopes;
    this.loading = false;
    const selected = scopes.find(scope => scope.scopeId === previousScope) || scopes[FIRST_PAGE];
    if (selected) { this.selectScope(selected.scopeId); }
  }

  private resetScope(): void {
    this.scopeChange$.next();
    this.configuration = null;
    this.selectedIds = [];
    this.page = FIRST_PAGE;
    this.query = EMPTY_TEXT;
    this.loading = true;
    this.saving = false;
    this.saved = false;
    this.requiresReload = false;
    this.errorKey = NO_ERROR;
  }

  private acceptConfiguration(configuration: StandardPinConfiguration): void {
    this.configuration = configuration;
    this.selectedIds = configuration.standards.map(standard => standard.id);
    this.loading = false;
    this.saving = false;
    this.requiresReload = false;
  }

  private loadFailed(): void {
    this.loading = false;
    this.errorKey = ERROR_LOAD;
  }

  private saveFailed(status: number): void {
    this.saving = false;
    this.requiresReload = true;
    this.errorKey = status === HTTP_CONFLICT ? ERROR_CONFLICT : ERROR_SAVE;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/admin/standards-management/standard-pins.component.ts